import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type AuditResponse } from "../../lib/api";
import { formatDateTime, formatNumber } from "../../lib/format";
import {
  Bar,
  Card,
  ChipToggle,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  useToast,
} from "../../components/ui";

const TYPES = [
  "PUBLISH",
  "REVIEW",
  "ACCESS",
  "ROLE",
  "INGEST",
  "VERSION",
  "QUERY",
  "SYNC",
  "AUTH",
  "ADMIN",
];

const TYPE_TINT: Record<string, { bg: string; fg: string }> = {
  PUBLISH: { bg: "#E6F7DC", fg: "#2F6420" },
  REVIEW: { bg: "#FCF6D2", fg: "#75650B" },
  ACCESS: { bg: "#EFECEE", fg: "#57515B" },
  ROLE: { bg: "#F3EEFA", fg: "#4B2E83" },
  INGEST: { bg: "#E4F5FB", fg: "#1D5F72" },
  VERSION: { bg: "#EFECEE", fg: "#57515B" },
  QUERY: { bg: "#F7FCF4", fg: "#2F6420" },
  SYNC: { bg: "#E4F5FB", fg: "#1D5F72" },
  AUTH: { bg: "#EFECEE", fg: "#57515B" },
  ADMIN: { bg: "#FFF3E9", fg: "#7A4A21" },
};

export function AuditScreen() {
  const toast = useToast();
  const [active, setActive] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", active, search, page],
    queryFn: () =>
      api.get<AuditResponse>("/admin/audit", {
        type: active ? [active] : undefined,
        q: search || undefined,
        page,
        pageSize: 50,
      }),
  });

  const exportCsv = () => {
    void api
      .blob("/admin/audit/export", { days: 365 })
      .then((url) => {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "audit-log.csv";
        anchor.click();
        URL.revokeObjectURL(url);
        toast("Audit log exported");
      })
      .catch(() => toast("Could not export the audit log", "danger"));
  };

  return (
    <>
      <PageHeader
        eyebrow="Compliance"
        title="Audit log"
        subtitle="Append-only record of access, retrieval and workflow events"
        actions={
          <button className="btn-ghost" onClick={exportCsv}>
            Export CSV
          </button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <ChipToggle
          label="All"
          active={active === null}
          onClick={() => {
            setActive(null);
            setPage(1);
          }}
        />
        {TYPES.map((type) => (
          <ChipToggle
            key={type}
            label={type.charAt(0) + type.slice(1).toLowerCase()}
            active={active === type}
            onClick={() => {
              setActive(active === type ? null : type);
              setPage(1);
            }}
          />
        ))}
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setSearch(query);
              setPage(1);
            }
          }}
          placeholder="Search events"
          aria-label="Search audit events"
          className="field-toolbar ml-auto max-w-[240px]"
        />
      </div>

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <Card padded={false}>
          {data?.items.length === 0 ? (
            <Empty title="No events match those filters" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left">
                <thead>
                  <tr className="table-head">
                    <th className="w-40 px-4 py-2.5">Time</th>
                    <th className="w-24 px-3 py-2.5">Type</th>
                    <th className="px-3 py-2.5">Event</th>
                    <th className="w-40 px-3 py-2.5">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((event) => {
                    const tint = TYPE_TINT[event.type] ?? TYPE_TINT.ACCESS;
                    return (
                      <tr
                        key={event.id}
                        className="border-b border-line-soft last:border-0 hover:bg-surface-hover"
                      >
                        <td className="px-4 py-2.5 text-2xs text-muted-soft">
                          {formatDateTime(event.occurredAt)}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className="pill"
                            style={{ background: tint.bg, color: tint.fg }}
                          >
                            {event.type}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-ink">
                          {event.summary}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-muted-strong">
                          {event.actor}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data && data.total > 50 && (
            <div className="flex items-center justify-between border-t border-line-soft px-4 py-2.5 text-sm text-muted">
              <span>
                Showing {(page - 1) * 50 + 1}–
                {Math.min(page * 50, data.total)} of {formatNumber(data.total)}
              </span>
              <span className="flex gap-2">
                <button
                  className="btn-ghost !py-1"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn-ghost !py-1"
                  disabled={page * 50 >= data.total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </span>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card title="Events this week" collapsible storageKey="events-this-week" defaultOpen={false}>
            {data?.breakdown.length === 0 ? (
              <p className="text-sm text-muted">Nothing recorded this week.</p>
            ) : (
              data?.breakdown.map((bar) => <Bar key={bar.label} {...bar} />)
            )}
          </Card>

          <Card title="Retention" collapsible storageKey="retention" defaultOpen={false}>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-soft">Policy</dt>
                <dd className="font-bold text-ink">
                  {data?.retentionYears ?? 7} years
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-soft">Events recorded</dt>
                <dd className="font-bold text-ink">
                  {formatNumber(data?.allTimeTotal ?? 0)}
                </dd>
              </div>
            </dl>
            <p className="mt-3 border-t border-line-soft pt-2.5 text-2xs leading-relaxed text-muted-soft">
              The log is append-only: nothing in the application updates or
              deletes a row. Answers the assistant declined are recorded too, so
              a refusal that touched a restricted document is still accounted
              for.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
